import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ProfileDropdown from "./ProfileDropdown";
import { useSessionContext } from "supertokens-auth-react/recipe/session";


import CreditCounter from "./CreditCounter";

const Navbar = ({ onOpenCreditModal, landingNav = false }) => {
  const session = useSessionContext();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isLanding = landingNav || location.pathname === '/';
  const navLinkClass = isLanding
    ? "text-white/90 font-semibold hover:text-white transition duration-300"
    : "text-primary font-semibold hover:text-primary/80 transition duration-300";
  const signInClass = navLinkClass;
  const mobileToggleClass = isLanding
    ? "p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
    : "p-2 rounded-lg text-primary hover:bg-gray-100 transition-colors";
  const mobileLinkClass = `block py-3 px-4 rounded-lg font-semibold ${
    isLanding ? "hover:bg-white/10" : "hover:bg-gray-100"
  }`;

  return (
    <header className="sticky top-0 z-40">
    <nav className={`${
      isLanding
        ? "bg-neutral-950 text-white border-b border-white/10"
        : "bg-white text-primary shadow-lg"
    } px-4 sm:px-6 md:px-8 py-3 sm:py-4 flex justify-between items-center relative`}>
      {/* Logo with transparent background (blue A + white paintbrush) */}
      <Link to="/" className="flex items-center bg-transparent flex-shrink-0" aria-label="yourshirt home">
        <img
          src={
            process.env.PUBLIC_URL
            + (isLanding ? '/yourshirt-logo-transparent.png?v=1' : '/yourshirt-logo.png?v=10')
          }
          alt="yourshirt"
          className="h-10 sm:h-12 md:h-14 w-auto max-w-[180px] sm:max-w-[220px] md:max-w-[260px] object-contain object-left"
        />
      </Link>

      {/* Desktop Nav Links - hidden on mobile */}
      <div className="hidden lg:flex items-center space-x-6">
        <Link
          to="/design"
          className={navLinkClass}
        >
          Generate
        </Link>
        <Link
          to="/products"
          className={`${navLinkClass} tour-products-nav`}
        >
          Products
        </Link>
        <Link
          to="/gallery"
          className={`${navLinkClass} tour-gallery-nav`}
        >
          Gallery
        </Link>
        <Link
          to="/designs"
          className={navLinkClass}
        >
          Ready Made
        </Link>
        <Link
          to="/help"
          className={navLinkClass}
        >
          Help
        </Link>

        {session.loading === false && session.doesSessionExist && (
          <>
            <Link
              to="/mockups"
              className={`${navLinkClass} tour-mockups-nav`}
            >
              Mockups
            </Link>
            <Link
              to="/orders"
              className={`${navLinkClass} tour-orders-nav`}
            >
              Order History
            </Link>
          </>
        )}

        {session.loading === false && session.doesSessionExist && session.accessTokenPayload["st-role"]?.v && session.accessTokenPayload["st-role"].v.includes("admin") && (
          <>
            <Link
              to="/admin"
              className={navLinkClass}
            >
              Admin
            </Link>
            <Link
              to="/admin/designs"
              className={navLinkClass}
            >
              Assets
            </Link>
          </>
        )}

        {session.loading === false && session.doesSessionExist && session.accessTokenPayload["st-role"]?.v && (session.accessTokenPayload["st-role"].v.includes("admin") || session.accessTokenPayload["st-role"].v.includes("facility_printful")) && (
          <Link
            to="/ordermanagement"
            className={navLinkClass}
          >
            Order Management
          </Link>
        )}

      </div>

      {/* Desktop Profile / Sign In - hidden on mobile */}
      <div className="hidden lg:flex items-center space-x-4">
        {session.loading === false && session.doesSessionExist && <CreditCounter onOpen={onOpenCreditModal} />}
        {session.loading === false && session.doesSessionExist ? (
          <ProfileDropdown navTone={isLanding ? "dark" : "light"} />
        ) : (
          <Link to="/auth" className={signInClass}>
            Sign In
          </Link>
        )}
      </div>

      {/* Mobile: Credits + Hamburger */}
      <div className="flex lg:hidden items-center gap-2">
        {session.loading === false && session.doesSessionExist && <CreditCounter onOpen={onOpenCreditModal} />}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={mobileToggleClass}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>
    </nav>

    {/* Mobile menu overlay */}
    {mobileMenuOpen && (
      <div
        className="lg:hidden fixed inset-0 z-30 bg-black/30"
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />
    )}

    {/* Mobile dropdown menu - z-50 so it's above the overlay and clicks reach the links */}
    {mobileMenuOpen && (
    <div
      className={`lg:hidden absolute top-full left-0 right-0 shadow-xl border-t overflow-y-auto max-h-[calc(100vh-4rem)] z-50 ${
        isLanding ? "bg-neutral-950 border-white/10 text-white" : "bg-white border-gray-200"
      }`}
    >
      <div className="px-4 py-4 space-y-1">
        <Link to="/design" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
          Generate
        </Link>
        <Link to="/products" className={`${mobileLinkClass} tour-products-nav`} onClick={() => setMobileMenuOpen(false)}>
          Products
        </Link>
        <Link to="/gallery" className={`${mobileLinkClass} tour-gallery-nav`} onClick={() => setMobileMenuOpen(false)}>
          Gallery
        </Link>
        <Link to="/designs" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
          Ready Made
        </Link>
        <Link to="/help" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
          Help
        </Link>
        {session.loading === false && session.doesSessionExist && (
          <>
            <Link to="/mockups" className={`${mobileLinkClass} tour-mockups-nav`} onClick={() => setMobileMenuOpen(false)}>
              Mockups
            </Link>
            <Link to="/orders" className={`${mobileLinkClass} tour-orders-nav`} onClick={() => setMobileMenuOpen(false)}>
              Order History
            </Link>
          </>
        )}
        {session.loading === false && session.doesSessionExist && session.accessTokenPayload["st-role"]?.v && session.accessTokenPayload["st-role"].v.includes("admin") && (
          <>
            <Link to="/admin" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
              Admin
            </Link>
            <Link to="/admin/designs" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
              Assets
            </Link>
          </>
        )}
        {session.loading === false && session.doesSessionExist && session.accessTokenPayload["st-role"]?.v && (session.accessTokenPayload["st-role"].v.includes("admin") || session.accessTokenPayload["st-role"].v.includes("facility_printful")) && (
          <Link to="/ordermanagement" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
            Order Management
          </Link>
        )}

        <div className={`border-t my-3 ${isLanding ? "border-white/10" : "border-gray-200"}`} />

        {session.loading === false && session.doesSessionExist ? (
          <div className="px-4 py-2">
            <ProfileDropdown navTone={isLanding ? "dark" : "light"} />
          </div>
        ) : (
          <Link to="/auth" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
            Sign In
          </Link>
        )}
      </div>
    </div>
    )}
    </header>
  );
};

export default Navbar;
